<template>
  <div class="devices-page">
    <!-- Background div that spans from left edge to right of filters column -->
    <div class="bg-section">
      <DevicesPage3d ref="devicesPage3d" model-path="/models/" class="3d" />
    </div>

    <!-- Filter Overlay component -->
    <FilterOverlay
        :is-visible="showFilterOverlay"
        :filter-type="activeFilterForOverlay"
        :initial-filter-options="currentFilterOptions"
        @close="closeFilterOverlay"
        @apply="applyAdvancedFilters"
    />
    <!-- Add Device Overlay component -->
    <AddDeviceOverlay
        :is-visible="showAddDeviceOverlay"
        @close="closeAddDeviceOverlay"
        @submit="handleAddDevice"
    />
    <ImportDeviceOverlay
        :is-visible="showImportDeviceOverlay"
        @close="closeImportDeviceOverlay"
        @submit="handleImportDevices"
    />

    <AppHeader title="Devices" />
    <div class="content">
      <div class="search-container">
        <!-- SEARCH BY section -->
        <div class="search-column">
          <h2 class="section-title">SEARCH BY :</h2>
          <div class="search-options">
            <button
                v-for="option in searchOptions"
                :key="option.id"
                :class="['search-option', { active: activeSearchOption === option.id }]"
                @click="toggleSearchOption(option.id)"
            >
              {{ option.label }}
            </button>
          </div>
          <div class="search-input-container">
            <input
                type="text"
                class="search-input"
                placeholder="Search..."
                v-model="searchQuery"
                @input="handleSearchInput"
            />
            <button class="search-button" @click="performSearch">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- FILTERS section -->
        <div class="filters-column">
          <h2 class="section-title">FILTERS :</h2>
          <div class="filter-sections">
            <div class="main-filters">
              <button
                  v-for="filter in filterOptions"
                  :key="filter.id"
                  :class="['filter-option', { active: activeFilterOptions.includes(filter.id), 'has-advanced-filters': hasAdvancedFilters(filter.id) }]"
                  @click="toggleFilterOption(filter.id)"
              >
                {{ filter.label }}
                <span
                    v-if="hasAdvancedFilters(filter.id)"
                    class="filter-indicator"
                    @click.stop="openFilterOverlay(filter.id)"
                ></span>
              </button>
            </div>
            <div class="sub-filters">
              <button
                  v-for="subFilter in subFilterOptions"
                  :key="subFilter.id"
                  :class="['sub-filter-option', 'disabled']"
                  disabled
              >
                {{ subFilter.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Action buttons section -->
        <div class="actions-column">
          <div class="action-buttons">
            <button class="action-button" @click="openAddDeviceOverlay">
              <span class="plus-icon">+</span> Add Device
            </button>
            <button
                class="action-button"
                @click="exportDevices"
                :disabled="isExporting"
            >
              <template v-if="!isExporting">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export Devices
              </template>
              <template v-else>
                Exporting...
              </template>
            </button>
            <button class="action-button" @click="openImportDeviceOverlay">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                   stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Import Devices
            </button>
          </div>
        </div>
      </div>

      <!-- Devices Table -->
      <DevicesTable
          :data="paginatedDevices"
          :columns="tableColumns"
          :loading="isLoading"
          :sort-column="sortColumn"
          :sort-direction="sortDirection"
          :pagination="true"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-items="totalItems"
          :selected-row="selectedDevice"
          row-key="ldn"
          @sort="handleSort"
          @row-select="selectDevice"
          @page-change="goToPage"
          @row-hover="handleRowHover"
      />
    </div>
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';
import DevicesTable from './DevicesTable.vue';
import FilterOverlay from './DevicesFilterOverlay.vue';
import DevicesPage3d from './DevicesPage3d.vue';
import { debounce } from 'lodash';
import DeviceApiService from '@/services/DeviceApiServices.js';
import AddDeviceOverlay from './AddDeviceOverlay.vue';
import ImportDeviceOverlay from './ImportDeviceOverlay.vue';
import { amrRawToM3, formatM3, hasAmrConversionMetadata } from '@/services/loraWbusHelper';

export default {
  name: 'DevicesPage',
  components: {
    AppHeader,
    DevicesTable,
    FilterOverlay,
    AddDeviceOverlay,
    ImportDeviceOverlay,
    DevicesPage3d,
  },
  data() {
    return {
      totalItems: 0,
      searchOptions: [
        { id: 'city', label: 'CITY' },
        { id: 'ldn', label: 'LDN' },
        { id: 'mpid', label: 'MPID' },
        { id: 'customerNumber', label: 'CUSTOMER N.' }
      ],
      filterOptions: [
        { id: 'mechanical', label: 'MECHANICAL' },
        { id: 'dlms', label: 'DLMS' },
        { id: 'lora_wbus', label: 'LORA & WBUS' },
        { id: 'ctr', label: 'CTR' },
      ],
      subFilterOptions: [
        { id: 'caliber', label: 'Caliber' },
        { id: 'diagnostics', label: 'Diagnostics' }
      ],
      activeSearchOption: '',
      activeFilterOptions: [],
      activeSubFilter: '',
      searchQuery: '',
      tableColumns: [
        { id: 'ldn', label: 'LDN', sortable: true },
        { id: 'customer_number', label: 'CUSTOMER NUMBER', sortable: true },
        { id: 'mpid', label: 'MPID', sortable: true },
        { id: 'reading', label: 'READING', sortable: true },
        { id: 'lastReadingDate', label: 'DATE LAST READING', sortable: true, type: 'date' },
        { id: 'diagnostics', label: 'DIAGNOSTICS', sortable: true },
        { id: 'readBy', label: 'READED BY', sortable: true },
        { id: 'city', label: 'CITY', sortable: true },
        { id: 'deviceType', label: 'DEVICE TYPE', sortable: true },
        { id: 'caliber', label: 'CALIBER', sortable: true }
      ],
      sortColumn: 'ldn',
      sortDirection: 'asc',
      filteredDevices: [],
      paginatedDevices: [],
      isLoading: true,
      currentPage: 1,
      isExporting: false,
      pageSize: 10,
      selectedDevice: null,
      showFilterOverlay: false,
      showAddDeviceOverlay: false,
      showImportDeviceOverlay: false,
      activeFilterForOverlay: '',
      advancedFilterOptions: {
        mechanical: { hasRadio: '', hasMpid: '', hasReadings: '' },
        dlms: { isIntegrated: '', attachedToMeter: '', hasMpid: '', hasReadings: '' },
        lora_wbus: { gateway: '', busType: '', hasMpid: '', hasReadings: '' }
      },
      filtersWithAdvancedOptions: []
    };
  },
  computed: {
    currentFilterOptions() {
      return this.advancedFilterOptions[this.activeFilterForOverlay] || {};
    }
  },
  watch: {
    activeFilterOptions: 'fetchDevices',
    activeSubFilter: 'fetchDevices',
    sortColumn: 'fetchDevices',
    sortDirection: 'fetchDevices',
    currentPage: 'fetchDevices'
  },
  methods: {
    toggleSearchOption(optionId) {
      this.activeSearchOption =
          this.activeSearchOption === optionId ? null : optionId;
      this.performSearch();
    },
    toggleFilterOption(filterId) {
      if (this.activeFilterOptions.includes(filterId)) {
        this.activeFilterOptions = this.activeFilterOptions.filter(id => id !== filterId);
        this.filtersWithAdvancedOptions = this.filtersWithAdvancedOptions.filter(id => id !== filterId);
      } else {
        this.activeFilterOptions.push(filterId);
        this.openFilterOverlay(filterId);
      }
      this.currentPage = 1;
    },
    setActiveSubFilter(filterId) {
      this.activeSubFilter = filterId;
      this.fetchDevices();
    },
    handleSearchInput: debounce(function () {
      this.performSearch();
    }, 300),
    performSearch() {
      this.currentPage = 1;
      this.fetchDevices();
    },
    async fetchDevices() {
      this.isLoading = true;
      try {
        const filterPayload = {
          searchQuery: this.searchQuery,
          searchField: this.activeSearchOption,
          deviceTypes: this.activeFilterOptions,
          subFilter: this.activeSubFilter,
          sortColumn: this.sortColumn,
          sortDirection: this.sortDirection,
          advancedFilters: this.createAdvancedFiltersObject(),
          currentPage: this.currentPage,
          pageSize: this.pageSize
        };

        // Use DeviceApiService to fetch devices
        const data = await DeviceApiService.getDevices(filterPayload);

        // Process the devices to ensure proper field mapping for the table
        if (data.devices && data.devices.length > 0) {
          // Map the fields from API response to what the table component expects
          data.devices = data.devices.map(device => {
            const isLoraWbus = this.isLoraWbusReadingDevice(device);
            const rawReading = device.rawReading ?? '';
            const displayReading = isLoraWbus ? (device.rawReading ?? device.reading ?? '') : (device.reading ?? device.rawReading ?? '');
            return {
              // Preserve all original properties
              ...device,
              // Map logical_device_number to ldn for table sorting/display
              ldn: device.logical_device_number || '',
              // Make sure deviceType is set properly for the DEVICE TYPE column
              deviceType: device.type || '',
              // Ensure other expected fields have values or empty strings
              rawReading,
              reading: this.formatDeviceReading(displayReading, device),
              lastReadingDate: device.lastReadingDate || null,
              diagnostics: device.diagnostics || '',
              readBy: device.readBy || '',
              // Ensure the rowKey value is set
              [this.tableColumns.find(col => col.id === 'ldn').id]: device.logical_device_number || ''
            };
          });
        }

        this.filteredDevices = data.devices || [];
        this.paginatedDevices = this.filteredDevices;

        // Set the total count from the API response for pagination
        this.totalItems = data.totalCount || 0;

      } catch (error) {
        console.error('Error fetching devices:', error);
        // Reset data in case of error
        this.filteredDevices = [];
        this.paginatedDevices = [];
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },
    isLoraWbusReadingDevice(device) {
      const protocol = String(device?.protocol || '').toLowerCase();
      const type = String(device?.type || device?.deviceType || '').toLowerCase();

      return (
          protocol.includes('lora') ||
          protocol.includes('wbus') ||
          type === 'lora' ||
          type === 'wbus' ||
          type === 'lora_wbus' ||
          (type.includes('lora') && type.includes('wbus'))
      );
    },
    formatDeviceReading(reading, device) {
      if (reading === null || reading === undefined || reading === '') return '';
      if (!this.isLoraWbusReadingDevice(device)) return reading;

      if (!hasAmrConversionMetadata(device)) return reading;

      const m3Value = amrRawToM3(reading, device);
      if (m3Value === null) return reading;

      return formatM3(m3Value);
    },
    createAdvancedFiltersObject() {
      const advancedFilters = {};
      this.activeFilterOptions.forEach(filterType => {
        if (this.hasAdvancedFilters(filterType)) {
          advancedFilters[filterType] = { ...this.advancedFilterOptions[filterType] };
        }
      });
      return advancedFilters;
    },
    handleSort({ column, direction }) {
      this.sortColumn = column;
      this.sortDirection = direction;
    },
    goToPage(page) {
      this.currentPage = page;
    },
    selectDevice(device) {
      this.selectedDevice = device;
    },
    openFilterOverlay(filterId) {
      this.activeFilterForOverlay = filterId;
      this.showFilterOverlay = true;
    },
    closeFilterOverlay() {
      this.showFilterOverlay = false;
    },
    applyAdvancedFilters({ filterType, options }) {
      this.advancedFilterOptions[filterType] = { ...options };
      if (!this.filtersWithAdvancedOptions.includes(filterType)) {
        this.filtersWithAdvancedOptions.push(filterType);
      }
      if (!this.activeFilterOptions.includes(filterType)) {
        this.activeFilterOptions.push(filterType);
      }
      this.fetchDevices();
    },
    hasAdvancedFilters(filterId) {
      return this.filtersWithAdvancedOptions.includes(filterId);
    },
    openAddDeviceOverlay() {
      this.showAddDeviceOverlay = true;
    },

    closeAddDeviceOverlay() {
      this.showAddDeviceOverlay = false;
    },
    openImportDeviceOverlay() {
      this.showImportDeviceOverlay = true;
    },
    closeImportDeviceOverlay() {
      this.showImportDeviceOverlay = false;
    },

    async exportDevices() {
      if (this.isExporting) {
        return; // Prevent multiple simultaneous exports
      }

      try {
        this.isExporting = true;

        // Prepare the export payload with current filters
        const exportPayload = {
          searchQuery: this.searchQuery,
          searchField: this.activeSearchOption,
          deviceTypes: this.activeFilterOptions,
          subFilter: this.activeSubFilter,
          sortColumn: this.sortColumn,
          sortDirection: this.sortDirection,
          advancedFilters: this.createAdvancedFiltersObject()
        };

        // Get all filtered devices from the export endpoint
        const allDevices = await DeviceApiService.getAllDevicesForExport(exportPayload);

        if (!allDevices || allDevices.length === 0) {
          alert('No devices found to export with current filters');
          return;
        }

        // Convert to CSV and trigger download
        this.downloadCSV(allDevices);

      } catch (error) {
        console.error('Error exporting devices:', error);
        alert('Failed to export devices. Please try again.');
      } finally {
        this.isExporting = false;
      }
    },

    downloadCSV(devices) {
      try {
        // Prepare CSV headers matching your table columns
        const headers = [
          'LDN',
          'CUSTOMER NUMBER',
          'MPID',
          'READING',
          'DATE LAST READING',
          'DIAGNOSTICS',
          'READ BY',
          'CITY',
          'DEVICE TYPE',
          'CALIBER'
        ];

        // Map devices to CSV rows
        const rows = devices.map(device => {
          // Extract device properties, handling the field mappings from your backend
          const ldn = device.ldn || device.logical_device_number || '';
          const customerNumber = device.customer_number || '';
          const mpid = device.mpid || '';
          const readingSource = this.isLoraWbusReadingDevice(device)
              ? (device.rawReading ?? device.reading)
              : (device.reading ?? device.rawReading);
          const reading = this.formatDeviceReading(readingSource, device);
          const lastReadingDate = this.formatDateForExport(device.lastReadingDate);
          const diagnostics = device.diagnostics || '';
          const readBy = device.readBy || '';
          const city = device.city || '';
          const deviceType = device.deviceType || device.type || '';
          const caliber = device.caliber || '';

          return [
            this.escapeCSVValue(ldn),
            this.escapeCSVValue(customerNumber),
            this.escapeCSVValue(mpid),
            this.escapeCSVValue(reading),
            this.escapeCSVValue(lastReadingDate),
            this.escapeCSVValue(diagnostics),
            this.escapeCSVValue(readBy),
            this.escapeCSVValue(city),
            this.escapeCSVValue(deviceType),
            this.escapeCSVValue(caliber)
          ];
        });

        // Create CSV content
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.join(','))
        ].join('\n');

        // Add BOM for Excel UTF-8 compatibility
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvContent;

        // Create blob and trigger download
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `devices_export_${this.getCurrentDateTime()}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        setTimeout(() => URL.revokeObjectURL(url), 100);

      } catch (error) {
        console.error('Error creating CSV:', error);
        throw error;
      }
    },

    escapeCSVValue(value) {
      if (value === null || value === undefined) {
        return '';
      }

      // Convert to string
      value = String(value);

      // Check if value needs to be quoted
      if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');
        // Wrap in quotes
        return `"${value}"`;
      }

      return value;
    },

    formatDateForExport(dateValue) {
      if (!dateValue) return '';

      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return String(dateValue);

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${hours}:${minutes} ${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return String(dateValue);
      }
    },
    getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}${month}${day}_${hours}${minutes}`;
    },
    async handleImportDevices(devices) {
      try {
        await DeviceApiService.importDevices(devices);
        this.fetchDevices();
        this.closeImportDeviceOverlay();
      } catch (error) {
        console.error('Error importing devices:', error);
      }
    },

    setupMouseListeners() {
      // Listen for mouse events on the entire page container
      const pageContainer = this.$el;
      if (!pageContainer) {
        console.error('Page container not found for mouse listeners');
        return;
      }

      const onMouseMove = (event) => {
        const rect = pageContainer.getBoundingClientRect();

        // Calculate mouse position relative to the page
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Normalize to -1 to 1 range
        const normalizedX = (mouseX / rect.width) * 2 - 1;
        const normalizedY = -(mouseY / rect.height) * 2 + 1;

        // Pass mouse position to 3D component
        if (this.$refs.devicesPage3d) {
          this.$refs.devicesPage3d.updateMousePosition(normalizedX, normalizedY);
        }
      };

      const onMouseLeave = () => {
        if (this.$refs.devicesPage3d) {
          this.$refs.devicesPage3d.resetMousePosition();
        }
      };

      // Add event listeners
      pageContainer.addEventListener('mousemove', onMouseMove, { passive: true });
      pageContainer.addEventListener('mouseleave', onMouseLeave, { passive: true });

      // Store references for cleanup
      this._pageMouseMove = onMouseMove;
      this._pageMouseLeave = onMouseLeave;
    },
    handleRowHover(hoverData) {
      if (!this.$refs.devicesPage3d) return;

      const {  isHovering, deviceType, caliber } = hoverData;

      if (isHovering) {
        // Determine which 3D model to focus on based on device type and caliber
        let targetModelIndex = this.getModelIndexForDevice(deviceType, caliber);

        // Tell 3D component to focus on specific model
        this.$refs.devicesPage3d.focusOnModel(targetModelIndex);
      } else {
        // Reset to normal view when not hovering
        this.$refs.devicesPage3d.resetCameraFocus();
      }
    },

    getModelIndexForDevice(deviceType, caliber) {
      // Map device types to model indices based on your requirements
      // Models are loaded in this order: octave2, sonata2, pegasus2, addon2
      // So indices are: 0=octave2, 1=sonata2, 2=pegasus2, 3=addon2

      deviceType = deviceType.toLowerCase();

      if (deviceType === 'mechanical') {
        return 2; // pegasus2
      }

      if (deviceType === 'addon') {
        return 3; // addon2
      }

      if (deviceType === 'meter') {
        // Check caliber - extract first digits
        const caliberNum = parseInt(caliber) || 0;
        if (caliberNum >= 40) {
          return 0; // octave2
        } else {
          return 1; // sonata2
        }
      }

      // Default fallback
      return 1; // sonata2
    },

    getModelNameForIndex(index) {
      const modelNames = ['octave2', 'sonata2', 'pegasus2', 'addon2'];
      return modelNames[index] || 'unknown';
    },

    handleAddDevice(deviceData) {
      // Call the API service to add the device
      DeviceApiService.createDevice(deviceData)

          .catch(error => {
            console.error('Error adding device:', error);
            // Here you would typically show an error message to the user
          });
    },
  },
  mounted() {
    this.fetchDevices();
    // Listen to global refresh requests (e.g., MPID removed in details view)
    window.addEventListener('devices:reload', this.fetchDevices);

    this.$nextTick(() => {
      this.setupMouseListeners();
    });
  },
  beforeUnmount() {
    window.removeEventListener('devices:reload', this.fetchDevices);

    if (this.$el && this._pageMouseMove && this._pageMouseLeave) {
      this.$el.removeEventListener('mousemove', this._pageMouseMove);
      this.$el.removeEventListener('mouseleave', this._pageMouseLeave);
    }
  },

};
</script>


<style scoped>
.login-page {
  background: linear-gradient(to bottom, #111111 0%, #1A1A1A 100%);
  min-height: 100vh;
  width: 100%;
  color: white;
  font-family: 'Inter', sans-serif;
}

.left-column {
  min-height: 60vh;
}

.right-column {
  background-color: rgba(86, 86, 86, 0.1);
  min-height: 40vh;
}

.error-message {
  margin-bottom: 16px;
  padding: 10px;
  background-color: rgba(255, 0, 0, 0.2);
  color: #ffdddd;
  text-align: center;
  border-radius: 4px;
  font-size: 14px;
}

@media (max-width: 767.98px) {
  .left-column {
    min-height: 60vh;
    order: 1;
  }

  .right-column {
    min-height: 40vh;
    order: 2;
  }

  .tagline {
    font-size: 36px !important;
  }

  .fluid-shape {
    width: 350px;
  }

  .left-content {
    padding-left: 10% !important;
    padding-right: 10% !important;
  }
}

.left-content {
  padding-left: 15%;
  padding-right: 5%;
}

.logo-container {
  margin-bottom: 40px;
}

.fluid-shape {
  max-width: 350px;
}

.tagline {
  font-size: 64px;
  font-weight: 400;
  line-height: 145%;
  color: #ffffff;
  margin-top: 30px;
}

.login-form-container {
  width: 100%;
  max-width: 450px;
}

.login-header {
  margin-bottom: 50px;
}

.login-to {
  font-size: 13px;
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 8px;
  color: #ffffff;
  letter-spacing: 3px;
}

.your-account {
  font-size: 32px;
  font-weight: 400;
  margin-top: 15px;
  color: #ffffff;
  letter-spacing: 3px;
}

.form-group {
  margin-bottom: 24px;
}

label {
  display: block;
  font-size: 11px;
  font-weight: 400;
  margin-bottom: 10px;
  color: #ffffff;
}

input {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background-color: transparent;
  color: white;
  font-size: 14px;
}

input:focus {
  outline: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
}

.remember-me {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  font-size: 11px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  height: 16px;
  width: 16px;
  background-color: transparent;
  border: 1px solid #ffffff;
  margin-right: 8px;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #ffffff;
}

.login-button-container {
  text-align: right;
}

.login-btn {
  padding: 10px 40px;
  background-color: transparent;
  color: white;
  border: 1px solid #ffffff;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.login-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>


<style scoped>
.devices-page {
  min-height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;

  position: relative;
}

/* Background section that spans from left edge to right of filters column */
.bg-section {
  position: absolute;
  top: 0;
  left: 33%; /* Start from roughly where the filters column begins */
  width: 67%; /* Cover the rest of the screen */
  height: 100%;
  background-color: rgba(86, 86, 86, 0.1); /* #565656 with 10% opacity */
  z-index: 0;
}


.content {
  padding: 100px 110px;
  position: relative;
  z-index: 1;
}

.search-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
  text-transform: uppercase;
  text-align: left;
}

/* Search Column */
.search-column {
  flex: 1;
}

.search-options {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding-left: 25px;
}

.search-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.search-option:hover,
.search-option.active {
  opacity: 1;
}

.search-input-container {
  position: relative;
  max-width: 300px;
  padding-left: 25px;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 12px;
}

.search-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  cursor: pointer;
}

/* Filters Column */
.filters-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.filter-sections {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 25px;
}

.main-filters {
  display: flex;
  justify-content: start;
  gap: 24px;
  margin-bottom: 16px;
  width: 100%;
}

.filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.filter-option:hover,
.filter-option.active {
  opacity: 1;
}

/* Advanced filter indicator dot */
.filter-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #4fc3f7;
  border-radius: 50%;
  margin-left: 6px;
  cursor: pointer;
}

.filter-option.has-advanced-filters::after {
  content: '';

}

.sub-filters {
  display: flex;
  justify-content: start;
  gap: 32px;
  width: 100%;
  margin-top: 0;
  padding-bottom: 16px;
}

.sub-filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.05em;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.sub-filter-option.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.sub-filter-option:hover,
.sub-filter-option.active {
  opacity: 1;
}

/* Actions Column */
.actions-column {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.plus-icon {
  font-size: 16px;
  font-weight: bold;
}





</style>
